import React, { useEffect } from 'react';
import moment from 'moment';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Heading, Body, Subtitle } from '../modules/Typography';
import Table from '../modules/Table';
import Paper from '../modules/Paper';
import Loading from '../modules/Loading';
import { GET_CATEGORY } from '../../queries';
import parseMarkdown from '../../utils/markdownParser';

export const CategoryContext = React.createContext();

const Category = () => {
    const { catSlug } = useParams();
    const navigate = useNavigate();

    // SIP-541: Fetch data, loading, and error from useQuery for GET_CATEGORY
    const { data, loading, error } = useQuery(GET_CATEGORY, { variables: { slug: catSlug } });

    // SIP-541: Safely access categorieses and category using optional chaining
    const categorieses = data?.categorieses;
    const category = categorieses?.data?.[0];

    // SIP-541: Effect to redirect to home if category data is not found after loading
    useEffect(() => {
        if (!loading && !error && (!categorieses || !categorieses.data || categorieses.data.length === 0)) {
            console.warn(`SIP-541: Category with slug '${catSlug}' not found or no data. Redirecting to home.`);
            navigate('/');
        }
    }, [loading, error, categorieses, navigate, catSlug]);

    // SIP-541: Display loading state
    if (loading) return <Loading />;

    // SIP-541: Handle GraphQL query errors
    if (error) {
        console.error('SIP-541: Error fetching category:', error);
        return <p>Error loading category. Please try again.</p>;
    }

    // SIP-541: Handle case where category is not found or data is malformed
    if (!category) {
        console.warn(`SIP-541: Category data for slug '${catSlug}' missing or malformed after loading.`);
        return <p>No data found for this category.</p>;
    }
    
    // SIP-541: Render category content, assuming 'category' data is valid
    return (
        <div>
            <Helmet><title>Disney Media Tech Specs</title></Helmet>
            <div className='Document'>
                <Heading number={5} heavy block className='Document__title'>{category.attributes.name}</Heading>
                <Paper className='flex column' elevation={6}>
                    <Body className='Document__text' number={2}>{parseMarkdown(category.attributes.description)}</Body>
                    {/* SIP-541: Render subcategories with existence checks */}
                    {category.attributes.subcategoryContainer
                        ? category.attributes.subcategoryContainer 
                            .filter(container => container && container.subcategory && container.subcategory.data)
                            .map(container => (
                                <div className='Document__Subcategory' key={container.subcategory.data.id}>
                                    <Link to={`/${category.attributes.slug}/${container.subcategory.data.attributes.slug}/${container.subcategory.data.attributes.documentContainer[0].document.data.attributes.slug}`}>
                                        <Subtitle number={3}>{container.subcategory.data.attributes.name}</Subtitle>
                                    </Link>
                                    <Body className='Document__text' number={2}>{parseMarkdown(container.subcategory.data.attributes.description)}</Body>
                                </div>
                            ))
                        : null
                    }
                </Paper>
                {/* SIP-541: Render updates if available */}
                {category.attributes.updates?.length ? ( 
                    <Paper className='Document__Updates'>
                        <Heading number={5} heavy block className='Document__Updates__title'>Updates</Heading>
                        <Table
                            data={category.attributes.updates
                                .sort((a, b) => moment(a.date).isBefore(moment(b.date)) ? -1 : 1)
                                .map(update => ([
                                    moment(update.date).format('L'),
                                    update.category,
                                    parseMarkdown(update.description)
                                ]))}
                            className='Document__Updates__table'
                        />
                    </Paper>
                ) : ''}
            </div>
        </div>
    );
};

export default Category;